import React, { useEffect, useState } from "react";
import { Trash2, Crosshair } from "lucide-react";
import { useT } from "../../lib/lang";
import { measureLabelForFeature } from "./PlanificationObjectList";

const OBJECT_COLORS = ["#00B894", "#3b82f6", "#ef4444", "#f59e0b", "#10b981", "#a855f7", "#ec4899"];

// Iter99 — Phase 2C §5.3/§7.2: edit panel for whichever object is
// currently SELECTED on the map or in the list (any object_type).
// "Annuler" restores nothing itself — it simply closes without ever
// calling onSave, so no history entry is created either way (spec:
// "Le bouton Annuler du formulaire doit restaurer les valeurs
// précédentes sans créer d'entrée d'historique").
export const PlanificationObjectPropertiesForm = ({ feature, onSave, onCancel, onDelete, onCenterOnMap }) => {
  const [t] = useT();
  const [name, setName] = useState(feature.properties.name || "");
  const [description, setDescription] = useState(feature.properties.description || "");
  const [color, setColor] = useState(feature.properties.color);
  const [opacity, setOpacity] = useState(feature.properties.opacity);

  // Re-sync local draft whenever the SELECTION changes to a different
  // feature (not on every re-render of the same one — the live geometry
  // ref below reads straight off `feature` instead, so a drag-move while
  // this panel is open never clobbers an in-progress name/color edit).
  useEffect(() => {
    setName(feature.properties.name || "");
    setDescription(feature.properties.description || "");
    setColor(feature.properties.color);
    setOpacity(feature.properties.opacity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature.id]);

  const isAnnotation = feature.properties.object_type === "annotation";
  const gps = isAnnotation ? feature.geometry.coordinates : null;
  const measureLabel = measureLabelForFeature(feature);

  return (
    <div className="flex flex-col gap-3 px-3 py-3" data-testid="planification-properties-form">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-zinc-300">{t.planificationProperties}</span>
        <button
          type="button"
          data-testid="planification-properties-center-btn"
          onClick={onCenterOnMap}
          title={t.planificationCenterOnMap}
          aria-label={t.planificationCenterOnMap}
          className="text-zinc-500 hover:text-emerald-300 p-1"
        >
          <Crosshair className="w-3.5 h-3.5" />
        </button>
      </div>

      {measureLabel && (
        <div className="text-[11px] font-mono text-emerald-300" data-testid="planification-properties-measure">{measureLabel}</div>
      )}

      <label className="flex flex-col gap-1 text-[11px] text-zinc-400">
        {t.planificationNameField}
        <input
          type="text"
          data-testid="planification-properties-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-[#0b0f14] border border-zinc-700 px-2 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-emerald-500"
        />
      </label>

      <label className="flex flex-col gap-1 text-[11px] text-zinc-400">
        {t.planificationDescriptionField}
        <textarea
          rows={2}
          data-testid="planification-properties-description-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="bg-[#0b0f14] border border-zinc-700 px-2 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-emerald-500 resize-none"
        />
      </label>

      <div className="flex flex-col gap-1 text-[11px] text-zinc-400">
        {t.planificationColorField}
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="planification-properties-color-swatches">
          {OBJECT_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`planification-properties-color-${c.slice(1)}`}
              onClick={() => setColor(c)}
              aria-label={c}
              aria-pressed={color === c}
              className={`w-5 h-5 rounded-full border-2 transition-transform ${color === c ? "border-white scale-110" : "border-transparent"}`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      </div>

      <label className="flex flex-col gap-1 text-[11px] text-zinc-400">
        {t.planificationOpacityField} — {Math.round(opacity * 100)}%
        <input
          type="range"
          min={0.1}
          max={1}
          step={0.05}
          data-testid="planification-properties-opacity-input"
          value={opacity}
          onChange={(e) => setOpacity(parseFloat(e.target.value))}
          className="accent-emerald-500"
        />
      </label>

      {isAnnotation && (
        <div className="flex flex-col gap-1 text-[11px] text-zinc-400">
          {t.planificationGpsCoordinates}
          <span className="font-mono text-zinc-300" data-testid="planification-properties-gps-readonly">
            {gps[1].toFixed(6)}, {gps[0].toFixed(6)}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between pt-1">
        <button
          type="button"
          data-testid="planification-properties-delete-btn"
          onClick={onDelete}
          title={t.planificationDeleteAction}
          aria-label={t.planificationDeleteAction}
          className="text-zinc-500 hover:text-red-400 p-1.5 border border-zinc-700 hover:border-red-400/60 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="planification-properties-cancel-btn"
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-medium text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 transition-colors"
          >
            {t.planificationCancelAction}
          </button>
          <button
            type="button"
            data-testid="planification-properties-save-btn"
            onClick={() => onSave({ name: name.trim(), description: description.trim(), color, opacity })}
            className="px-3 py-1.5 text-[11px] font-semibold text-black bg-emerald-500 hover:bg-emerald-400 transition-colors"
          >
            {t.planificationSave}
          </button>
        </div>
      </div>
    </div>
  );
};
